import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Grommet,
  Box,
  Grid,
  Heading,
  Text,
  Button,
  RadioButtonGroup,
  TextInput,
  Image as GrommetImage,
  Main,
  ResponsiveContext,
} from 'grommet';
import styled from 'styled-components';

import { Co2, FoodRescue, Meals, Money } from '../MeshIcon'

// Custom theme
const customTheme = {
  global: {
    font: {
      family: 'Montserrat, sans-serif',
    },
    colors: {
      'gray-900': '#101828',
      'gray-700': '#344054', 
      'gray-600': '#475467',
      'gray-300': '#D0D5DD',
      'base-white': '#FFFFFF',
      'light-green': '#03783D4D',
      'green-dark': '#03783D',
      'green-darker': '#1B4A3B',
      focus: 'transparent', 
      'custom-light-gray': '#F9FAFB',
      'custom-light-green': '#E6F1EC',
    'status-error': '#D9534F',
    },
    active: {
      background: 'transparent', 
      color: 'text',
    },
  },
  heading: {
    font: { family: 'Arvo' },
  },
  text: {
    font: { family: 'Montserrat, sans-serif' },
  },
  button: {
    border: { radius: '4px' },
    extend: `
      font-family: 'Montserrat, sans-serif';
      font-weight: 500;
      font-size: 12px;
    `,
  },
  radioButton: {
    check: { color: 'gray-700' },
    hover: { borderColor: 'gray-700' },
  },
  textInput: {
    container: {
      extend: `
        &:hover {
          // No specific hover style unless desired
        }
      `,
    },
    focus: {
      border: { // Try to control focus border if grommet theme adds one
        color: undefined, // Let the Box control it
      },
      boxShadow: 'none', 
    },
  },
};

const StyledInput = styled(TextInput)`
  border: none;
  text-align: center;
  padding: 8px;
  font-size: 24px;
  flex-grow: 1;
  font-family: 'Arvo';
  font-weight: normal;
  color: ${props => props.theme.global.colors['gray-600']};
  background: transparent;														  

  &:focus {
    outline: none;
    box-shadow: none !important; // Override any theme focus shadow
    border-color: transparent !important; // Prevent border color change if any
  }

  &[type='number']::-webkit-outer-spin-button,
  &[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }

  // Remove default Grommet focus styling more aggressively if needed
  &&& {
    &:focus {
        box-shadow: none !important;
    }
  }
`;

const StepperButton = styled(Button).attrs(() => ({
  plain: true, // Remove default Grommet button styling
  }))`
  background: transparent !important;
  border: none !important;
  font-size: 24px;
  padding: 0 16px;
  cursor: pointer;
  border-radius: 4px;
  font-family: 'Arvo';
  font-weight: normal;
  color: ${props => props.theme.global.colors['gray-700']} !important;
  height: 100%;
  min-height: 48px; /* Corresponds to 50px container - 2px border */
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
    color: ${props => props.theme.global.colors['gray-700']} !important;
    box-shadow: none !important;
    outline: none !important;
  }
`;

const MoneyValueText = styled(Text)`
  font-size: clamp(20px, 4vw, 48px);
  font-weight: bold;
  font-family: 'Arvo';
  text-align: center;
  width: 100%;
  white-space: nowrap;
`;

const StatValueText = styled(Text)`
  font-size: 24px;
  font-weight: normal;
  font-family: 'Arvo';
`;

// Helper to format numbers with thousand separators
const formatNumberWithCommas = (valueStr) => {
  if (!valueStr) return "";
  const parts = valueStr.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

// Helper to strip non-numeric characters for parsing
const stripNonNumeric = (valueStr) => {
  if (!valueStr) return "";
  return valueStr.replace(/[^\d.]/g, ""); // Allows digits and decimal point
};

const formatNumber = (value, isInteger = false) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return '0';
  }
  const num = Number(value);
  const decimals = isInteger ? 0 : 2;
  
  if (Math.abs(num) >= 1000000) {
    const millions = num / 1000000;
    const numberToFormatInMillions = parseFloat(millions.toFixed(decimals));
    const formattedMillionsString = numberToFormatInMillions.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return formattedMillionsString + 'M';
  } else if (Math.abs(num) >= 1000) {
    const thousands = num / 1000;
    const numberToFormatInThousands = parseFloat(thousands.toFixed(decimals));
    const formattedThousandsString = numberToFormatInThousands.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return formattedThousandsString + 'K';
  } else {
    return Math.round(num).toLocaleString('en-US');
  }
};

const ProgramCalculator = () => {
  const size = useContext(ResponsiveContext);
  const isSmall = size === 'small';

  const [storeCountNumber, setstoreCountNumber] = useState(8);
  const [storeSize, setstoreSize] = useState(30000);

  const [storeSizeInputValue, setstoreSizeInputValue] = useState('30,000');
  const [storeCountNumberInputValue, setstoreCountNumberInputValue] = useState('8');

  const [storeCountNumberError, setstoreCountNumberError] = useState('');
  const [storeSizeError, setstoreSizeError] = useState('');

  const [fdpMoney, setfdpMoney] = useState(0);
  const [cdpMoney, setcdpMoney] = useState(0);
  const [fcdpMoney, setfcdpMoney] = useState(0);
  const [foodFdpDiverted, setfoodFdpDiverted] = useState(0);
  const [co2eFdpAvoided, setco2eFdpAvoided] = useState(0);
  const [foodCdpDiverted, setfoodCdpDiverted] = useState(0);
  const [co2eCdpAvoided, setco2eCdpAvoided] = useState(0);
  const [mealsCdp, setmealsCdp] = useState(0);
  const [foodFcdpDiverted, setfoodFcdpDiverted] = useState(0);
  const [co2eFcdpAvoided, setco2eFcdpAvoided] = useState(0);
  const [mealsFcdp, setmealsFcdp] = useState(0);

  const storeCountNumberInputRef = useRef(null);
  const storeSizeInputRef = useRef(null); 
  
  useEffect(() => {
    const numStoreCountNumber = parseInt(storeCountNumberInputValue.replace(/\D/g, ''), 10) || 0;
    const numStoreSize = parseFloat(stripNonNumeric(storeSizeInputValue)) || 0;

    let proceedWithCalculations = true;
    // Store Room validation
    if (numStoreCountNumber == 0) {
      proceedWithCalculations = false;
    } else if (numStoreCountNumber > 50) {
      setstoreCountNumberError('Number of stores cannot exceed 50.');
      proceedWithCalculations = false;
    } else {
      setstoreCountNumberError('');
    }

    // Store Size validation
    if (numStoreSize == 0) {
      proceedWithCalculations = false;
    } else if (numStoreSize > 100000) {
      setstoreSizeError('Store size cannot exceed 100,000 sqft.');
      proceedWithCalculations = false;
    } else {
      setstoreSizeError('');
    }

    if (!proceedWithCalculations) {
      setfdpMoney(0); setfoodFdpDiverted(0); setco2eFdpAvoided(0);
      setcdpMoney(0); setfoodCdpDiverted(0); setco2eCdpAvoided(0); setmealsCdp(0);
      setfcdpMoney(0); setfoodFcdpDiverted(0); setco2eFcdpAvoided(0); setmealsFcdp(0);
      return;
    }

    if (storeCountNumber !== numStoreCountNumber && numStoreCountNumber <= 50) setstoreCountNumber(numStoreCountNumber);
    if (storeSize !== numStoreSize && numStoreSize <= 100000) setstoreSize(numStoreSize);

    const currentstoreCountNumber = (storeCountNumberError || numStoreCountNumber > 50) ? 0 : storeCountNumber;
    const currentstoreSize = (storeSizeError || numStoreSize > 100000) ? 0 : storeSize;
    
    const calcFdpMoney = (((currentstoreCountNumber * currentstoreSize * 2.89 ) / 1000 ) * 0.40 * 175 );
    const calcFdpFoodDiverted = ((currentstoreCountNumber * currentstoreSize * 2.89 ) * 0.40);
    const calcFdpCo2eAvoided = ((currentstoreCountNumber * currentstoreSize * 2.89 ) * 0.40) * 0.46;
    setfdpMoney(calcFdpMoney);
    setfoodFdpDiverted(calcFdpFoodDiverted);
    setco2eFdpAvoided(calcFdpCo2eAvoided);

    const calcCdpMoney = (((currentstoreCountNumber * currentstoreSize * 2.89 ) / 1000 ) * 0.50 * 175 );
    const calcCdpFoodDiverted = ((currentstoreCountNumber * currentstoreSize * 2.89 ) * 0.50);
    const calcCdpCo2eAvoided = ((currentstoreCountNumber * currentstoreSize * 2.89 ) * 0.50) * 3.36;
    const calcCdpMeals = ((currentstoreCountNumber * currentstoreSize * 2.89 ) * 0.50) * 0.544;
    setcdpMoney(calcCdpMoney);
    setfoodCdpDiverted(calcCdpFoodDiverted);
    setco2eCdpAvoided(calcCdpCo2eAvoided);
    setmealsCdp(calcCdpMeals);

    setfcdpMoney(((currentstoreCountNumber * currentstoreSize * 2.89 ) / 1000 ) * 0.90 * 175 );
    setfoodFcdpDiverted((currentstoreCountNumber * currentstoreSize * 2.89 ) * 0.90);
    setco2eFcdpAvoided((currentstoreCountNumber * currentstoreSize * 2.89  * 0.90 * 0.5 *3.36) +
     (currentstoreCountNumber * currentstoreSize * 2.89  * 0.90 * 0.4 * 0.46));
    setmealsFcdp(currentstoreCountNumber * currentstoreSize * 2.89 * 0.90 * 0.50 * 0.544);
  }, 
  [storeCountNumberInputValue, 
    storeSizeInputValue,
    storeCountNumber, 
    storeSize, 
    storeCountNumberError, 
    storeSizeError]);

  useEffect(() => {
    if (parseInt(storeCountNumberInputValue.replace(/\D/g, ''), 10) !== storeCountNumber) {
      setstoreCountNumberInputValue(storeCountNumber.toString());
    }
  }, [storeCountNumber]);

  useEffect(() => {
    const currentNumericInputValue = parseFloat(stripNonNumeric(storeSizeInputValue));
    if (currentNumericInputValue !== storeSize && !isNaN(storeSize)) {
        const isFocused = storeSizeInputRef.current === document.activeElement;
        if (!isFocused) {
             setstoreSizeInputValue(formatNumberWithCommas(storeSize.toString()));
        } else if (storeSize === 0 && storeSizeInputValue !== "0" && storeSizeInputValue !== "") {
             // If focused and cleared, or set to 0, reflect it if not already.
             // This part is tricky with live formatting; simpler to format on blur or rely on direct typing.
             // For now, we primarily format on change.
        }
    } else if (isNaN(storeSize) && storeSizeInputValue !== "") {
        setstoreSizeInputValue(""); // Clear input if number is NaN
    }
  }, [storeSize]);
  
  const handlestoreCountNumberInputFocus = () => {
    if (storeCountNumberInputValue === "0") setstoreCountNumber("");
  };

  const handlestoreCountNumberInputChange = (event) => {
    const val = event.target.value;
    const numericVal = val.replace(/\D/g, ''); // Allow only digits

    setstoreCountNumberInputValue(numericVal); // Update the input field's display value
    const parsedNum = parseInt(numericVal, 10);

    if (numericVal === "") {
        setstoreCountNumber(0);
    } else if (!isNaN(parsedNum) && parsedNum >= 0) {
        if (parsedNum > 50) {
            setstoreCountNumberError('Number of stores cannot exceed 50.');
            // Calculations will stop via useEffect
            setstoreCountNumber(parsedNum); 
        } else {
            setstoreCountNumberError('');
            setstoreCountNumber(parsedNum);
        }
    }
  };
  const handlestoreCountNumberInputBlur = () => {

    let numericVal = parseInt(storeCountNumberInputValue.replace(/\D/g, ''), 10);
    if (storeCountNumberInputValue === "" || isNaN(numericVal) || numericVal < 0) {
      setstoreCountNumberInputValue("0");
      setstoreCountNumber(0);
      setstoreCountNumberError(''); // Clear error if input is reset to valid 0
    } else if (numericVal > 50) {
      setstoreCountNumberError('Number of stores cannot exceed 50.');
      setstoreCountNumber(numericVal); // Keep the value that caused error
      setstoreCountNumberInputValue(numericVal.toString()); 
    } else {
      setstoreCountNumber(numericVal);
      setstoreCountNumberInputValue(numericVal.toString());
      setstoreCountNumberError('');
    }
  };
  
  const decrementStoreCount = () => {
    setstoreCountNumber(prev => {
        const newValue = Math.max(0, prev - 1);
        if (newValue <= 50 && storeCountNumberError && prev > 50) setstoreCountNumberError('');
        else if (newValue > 50) setstoreCountNumberError('Number of stores cannot exceed 50.');
        setstoreCountNumberInputValue(newValue.toString()); 
        return newValue;
    });
  };
  
  const incrementStoreCount = () => {
    setstoreCountNumber(prev => {
        const newValue = prev + 1;
        if (newValue > 50) {
            setstoreCountNumberError('Number of stores cannot exceed 50.');
        } else if (storeCountNumberError && prev <= 50) {
            setstoreCountNumberError('');
        }
        setstoreCountNumberInputValue(newValue.toString()); 
        return newValue; // Allow incrementing beyond 50 to show error correctly
    });
  };
  

  const handlestoreSizeFocus = () => {
    if (storeSizeInputValue === "0") {
      setstoreSizeInputValue("");
    }
  };

  const handlestoreSizeChange = (event) => {
    const rawValue = event.target.value;
    let preNumericString = "";
    let hasDecimal = false;
    for (let i = 0; i < rawValue.length; i++) {
        const char = rawValue[i];
        if (/\d/.test(char)) {
            preNumericString += char;
        } else if (char === '.' && !hasDecimal) {
            preNumericString += char;
            hasDecimal = true;
        } else if (char === ',' && /\d/.test(rawValue[i-1]) && /\d/.test(rawValue[i+1])) {
            // Allow commas if they are part of thousand separation for parsing, but stripNonNumeric will get it
        }
    }
    const cleanNumericString = stripNonNumeric(rawValue); 
    const formattedDisplayValue = formatNumberWithCommas(cleanNumericString);
    setstoreSizeInputValue(formattedDisplayValue);

    const parsedNum = parseFloat(cleanNumericString);

    if (cleanNumericString === "") {
        setstoreSize(0);
        setstoreSizeError('');
    } else if (!isNaN(parsedNum) && parsedNum >= 0) {
        if (parsedNum > 100000) {
            setstoreSizeError('Store size cannot exceed 100,000 sqft.');
        } else {
            setstoreSizeError('');
        }
        setstoreSize(parsedNum); 
    } else {
        // Potentially invalid intermediate state, like "1.2.3" or "abc"
        // Calculations will use last valid storeSize or 0 if parsing fails
    }
  };
  
  const handlestoreSizeBlur = () => {
    const cleanNumericString = stripNonNumeric(storeSizeInputValue);
    let numericVal = parseFloat(cleanNumericString);

    if (cleanNumericString === "" || isNaN(numericVal) || numericVal < 0) {
      setstoreSizeInputValue("0");
      setstoreSize(0);
      setstoreSizeError('');
    } else if (numericVal > 100000) {
      setstoreSizeError('Store size cannot exceed 100,000 sqft.');
      setstoreSize(numericVal); // Keep the value for error display
      setstoreSizeInputValue(formatNumberWithCommas(numericVal.toString())); // Show formatted error value
    } else {
      setstoreSize(numericVal);
      setstoreSizeInputValue(formatNumberWithCommas(numericVal.toString()));
      setstoreSizeError('');
    }
  };
  
  const gridColumns = isSmall ? ['auto'] : ['280px', '1fr', '1fr', '1fr'];
  const gridRows = isSmall ? ['auto', 'auto', 'auto', 'auto'] : ['auto'];
  const gridAreas = isSmall
    ? [ // Mobile layout: single column
      { name: 'form', start: [0, 0], end: [0, 0] },
      { name: 'program1', start: [0, 1], end: [0, 1] },
      { name: 'program2', start: [0, 2], end: [0, 2] },
      { name: 'combined', start: [0, 3], end: [0, 3] },
    ]
    : [ // Desktop layout: four columns
      { name: 'form', start: [0, 0], end: [0, 0] },
      { name: 'program1', start: [1, 0], end: [1, 0] },
      { name: 'program2', start: [2, 0], end: [2, 0] },
      { name: 'combined', start: [3, 0], end: [3, 0] },
    ];

  return (
    <Grommet theme={customTheme} full>
      <Main id="Main" background="white" align="center" justify="start" pad={{ top: 'small' }}>
        <Box
          id="Box1"
          width={{ max: '1280px', width: '100%' }}
          height={{ max: 'auto'}}
          background="white"
          round="small"
          elevation="small"
          overflow="hidden"
          flex={false}
          pad={{ bottom: "xsmall"}}																		   
          >
          {/*  <Box id="Box2" flex={false} fill="horizontal" background="white" height="10px"/> */}
          {/* <Box
            id="Box3"
            width="100%" 
            height={{ min: '70px' }}
            align="start"
            justify="center"
            pad={{ horizontal: 'medium', vertical: 'xsmall' }}
            flex={false}
            >
            <Text
              // margin="5px"
              size="36px"
              color="gray-900"
              style={{ fontFamily: 'Arvo, Sans', fontWeight: 'normal', textAlign: 'left' }}
              >
              See how much money you can save each year
            </Text>
          </Box> */}
          <Box
            id="Box5"
            width="100%"
            flex={{ shrink: 1, grow: 0 }}
            height={{ max: 'auto' }}
            pad={{ horizontal: 'medium', bottom: 'large' }}
            >
            <Grid
              id="MainGrid"
              rows={gridRows}
              columns={gridColumns}
              areas={gridAreas}
              gap="medium"
              >
              {/* Column 1: User Input */}
              <Box
                id="Box6"   
                background="white"
                pad={{ right: isSmall ? 'none' : 'medium', vertical: 'medium' }}
                round="small"
                flex={false}
                gap="medium"
                >
                <Text
                  id="StoreCountQuestion"
                  size={isSmall ? 'large' : '24px'}
                  style={{fontWeight: 'normal' }}
                  color="gray-600"
                  >
                  How many stores do you have?
                </Text>
                <Box direction="column" gap="xsmall">
                  <Box
                    direction="row"
                    align="center"
                    round="small"
                    // margin={{ vertical: 'small' }}
                    border={{ color: storeCountNumberError ? 'status-error' : 'gray-300', size: 'small' }}
                    id="storeCountNumberInput"
            
                    >
                      <StepperButton
                        label="–"
                        onClick={decrementStoreCount}
                        a11yTitle="Decrement Store Count"
                        />
                      <StyledInput
                        ref={storeCountNumberInputRef}
                        id="storeCountNumberInputField"
                        type="text" 
                        inputMode="numeric" 
                        value={storeCountNumber}
                        onChange={handlestoreCountNumberInputChange}
                        onFocus={handlestoreCountNumberInputFocus}
                        onBlur={handlestoreCountNumberInputBlur}
                        textAlign="center"
                      />
                      <StepperButton
                        label="+"
                        onClick={incrementStoreCount}
                        a11yTitle="Increment Store Count"
                      />
                    </Box>
                    {storeCountNumberError && (
                      <Text color="status-error"
                        style={{ fontSize: '12px', 
                                  fontFamily: 'Montserrat, sans-serif', 
                                  paddingLeft: 'small' }}>
                        {storeCountNumberError}
                      </Text>
                    )}	
                  </Box>
                <Text
                  id="storeSizeQuestion"
                  size={isSmall ? 'large' : '24px'} 
                  style={{ fontWeight: 'normal' }}
                  color="gray-600"
                  margin={{ top: 'medium' }}
                >
                  What is your average store size?
                </Text>
                <Box direction="column" gap="xsmall">
                  <Box
                    direction="row"
                    align="center"
                    id="storeSizeInput"
                    round="small"
                    border={{ color: storeSizeError ? 'status-error' : 'gray-300', size: 'small' }}
                    height={{ min: '50px' }}
                    focusIndicator={false} 
                              hoverIndicator={false} 
                  >
                    <StyledInput
                      ref={storeSizeInputRef}
                      id="storeSizeInputField"
                      type="text"
                      inputMode="decimal"
                      value={storeSizeInputValue}
                      onFocus={handlestoreSizeFocus}
                      onChange={handlestoreSizeChange}
                      onBlur={handlestoreSizeBlur}
                    />
                    <Text
                      size="medium"
                      style={{ fontSize: '24px', fontWeight: 'normal' }}
                      color="gray-600"
                      margin={{ right: 'small',  left: 'small' }}
                      >
                      sqft
                    </Text>
                  </Box>
                  {storeSizeError && (
                    <Text color="status-error"
                      style={{ fontSize: '12px', 
                                fontFamily: 'Montserrat, sans-serif', 
                                paddingLeft: 'small' }}>
                      {storeSizeError}
                    </Text>
                  )}
                </Box>
                <Box flex />
                <Text color="gray-600"
                  style={{ fontSize: '12px', 
                    fontFamily: 'Montserrat, sans-serif', 
                    paddingLeft: 'small' }}>
                 *These savings calculations are based on current waste market cost estimates.
                </Text>
              </Box>
              {/* Column 2: Program 1 Results */}
              <Box id="FdpResults" 
                gridArea="program1" 
                background="custom-light-gray" 
                pad="medium" 
                round="small" 
                align="center">								  
                <Box 
                  id="FdpHeadingBox" 
                  width="100%" 
                  height={{ min: '90px' }} 
                  align="center" 
                  justify="center"
                  >
                  <Text
                    margin="none"
                    color="gray-700"
                    style={{ fontFamily: 'Arvo, Sans', fontSize: '24px', 
                             fontWeight: 'bold', textAlign: 'center'}}
                    >
                    Farmer <br/>Diversion Program
                  </Text>
                </Box>
                <Box id="FdpSectionSpace"
                  flex={false}
                  fill="horizontal"
                  height="1px"
                  margin={{ vertical: 'small' }}
                  border={{ color: 'gray-300' }}
                />
                <Box
                  id="fdpMoneySection"
                  width="100%"
                  height={{ min: '120px' }}
                  align="center"
                  justify="center"  
                  margin={{ top: 'medium' }}
                  border={{ color: 'gray-300', size: 'small' }}
                  >
                  <Text
                    id="fdpMoneyLabel"
                    style={{ fontSize: '24px', fontWeight: 'normal' }}
                    color="gray-900"
                    margin={{ bottom: 'medium' }}
                    >
                    SAVE
                  </Text>
                  <MoneyValueText id="fdpMoneyValue" color="gray-600">
                    ${formatNumber(fdpMoney.toFixed(0))}
                  </MoneyValueText>
                </Box>
                <Box id="FdpStatsGridBox" width="100%" height="auto" margin={{ top: 'medium' }}>
                  <Grid
                    id="FdpStatsGrid"
                    rows={['1fr', '1fr', '1fr']}
                    columns={['2fr', '1fr']}
                    gap="small"
                    fill="horizontal"
                  >
                    <Box id="FdpFoodDiverted" justify="center">
                      <StatValueText color="gray-600">
                        {formatNumber(foodFdpDiverted.toFixed(0))} kg
                      </StatValueText>
                      <Text 
                        style= {{ fontSize: '12px',
                        fontWeight: 'normal' }} color="gray-700">of food diverted from landfill
                      </Text>
                    </Box>
                    <Box id="FdpFoodDivertedIcon" align="center" justify="center">
                      <FoodRescue 
                        size="52px" 
                        color={customTheme.global.colors['gray-300']} 
                      />
                    </Box>
                    <Box id="FdpCo2eAvoided" justify="center">
                      <StatValueText color="gray-600">
                        {formatNumber(co2eFdpAvoided.toFixed(0))} kg
                      </StatValueText>
                      <Text 
                        style= {{ fontSize: '12px',
                        fontWeight: 'normal' }} color="gray-700">CO2e emissions avoided
                      </Text>
                    </Box>
                    <Box id="FdpCo2eAvoidedIcon" align="center" justify="center">
                      <Co2 
                        size="52px" 
                        color={customTheme.global.colors['gray-300']} 
                      />
                    </Box>
                    <Box id="FdpBlankRow" />
                    <Box id="FdpBlankIcon" align="center" justify="center" />
                  </Grid>
                </Box>
                <Box id="FdpSectionSpace"
                   flex={false}
                   fill="horizontal"
                   height="1px"
                   margin={{ vertical: 'small' }}
                   border={{ color: 'gray-300' }}/>
                <Box id="FdpButtonBox" margin={{ top: 'medium' }} width="256px">
                  <Button
                    id="FdpLetsTalkButton"
                    primary color= {'gray-700'}
                    label={<Text fontSize={'18px'} color="base-white" >Let&apos;s Talk</Text>}
                    style={{
                      padding: '8px 16px',
                    }}
                    elevation="small"
                    fill="horizontal"
                    onClick={() => window.location.href = 'mailto:growth@foodmesh.ca'}
                  />
                </Box>
              </Box>
              {/* Column 3: Program 2 Results */}		
              <Box id="CdpResults" 
                gridArea="program2" 
                background="custom-light-gray" 
                pad="medium" 
                round="small" 
                align="center">
                <Box id="CdpHeadingBox" width="100%" height={{ min: '90px' }} align="center" justify="center">
                  <Text
                    margin="none"
                    color="gray-700"
                    style={{ fontFamily: 'Arvo, Sans', fontSize: '24px', 
                             fontWeight: 'bold', textAlign: 'center'}}
                  >
                    Charity <br/>Diversion Program
                  </Text>
                </Box>
                <Box id="CdpSectionSpace"
                   flex={false}
                   fill="horizontal"
                   height="1px"
                   margin={{ vertical: 'small' }}
                   border={{ color: 'gray-300' }}/>
                <Box
                  id="cdpMoneySection"
                  width="100%"
                  height={{ min: '120px' }}
                  align="center"
                  justify="center"
                  margin={{ top: 'medium' }}
                  border={{ color: 'gray-300', size: 'small' }}
                >
                  <Text
                    id="cdpMoneyLabel"
                    style={{ fontSize: '24px', fontWeight: 'normal' }}
                    color="gray-900"
                    margin={{ bottom: 'medium' }}
                  >
                    SAVE
                  </Text>
                  <MoneyValueText id="cdpMoneyValue" color="gray-600">
                    ${formatNumber(cdpMoney.toFixed(0))} 
                  </MoneyValueText>
                </Box>
                <Box id="CdpStatsGridBox" width="100%" height="auto" margin={{ top: 'medium' }}>
                  <Grid
                    id="CdpStatsGrid"
                    rows={['1fr', '1fr', '1fr']}
                    columns={['2fr', '1fr']}
                    gap="small"
                    fill="horizontal"
                  >
                    <Box id="CdpFoodDiverted" justify="center">
                      <StatValueText color="gray-600">{formatNumber(foodCdpDiverted.toFixed(0))} kg</StatValueText>
                      <Text 
                      style= {{ fontSize: '12px',
                      fontWeight: 'normal' }} color="gray-700">of food diverted from landfill</Text>
                    </Box>
                    <Box id="CdpFoodDivertedIcon" align="center" justify="center">
                      <FoodRescue 
                        size="52px" 
                        color={customTheme.global.colors['gray-300']} 
                      />
                    </Box>
                    <Box id="CdpCo2eAvoided" justify="center">
                      <StatValueText color="gray-600">{formatNumber(co2eCdpAvoided.toFixed(0))} kg</StatValueText>
                      <Text 
                      style= {{ fontSize: '12px',
                      fontWeight: 'normal' }} color="gray-700">CO2e emissions avoided</Text>
                    </Box>
                    <Box id="CdpCo2eAvoidedIcon" align="center" justify="center">
                      <Co2 
                        size="52px" 
                        color={customTheme.global.colors['gray-300']} 
                      />
                    </Box>
                    <Box id="CdpMeals" justify="center">
                      <StatValueText color="gray-600">{formatNumber(mealsCdp.toFixed(0), true)}</StatValueText>
                      <Text 
                      style= {{ fontSize: '12px',
                      fontWeight: 'normal' }} color="gray-700">meals for your community</Text>
                    </Box>
                    <Box id="CdpMealsIcon" align="center" justify="center">
                      <Meals 
                        size="52px" 
                        color={customTheme.global.colors['gray-300']} 
                      />
                    </Box>
                  </Grid>
                </Box>
                <Box id="CdpSectionSpace"
                   flex={false}
                   fill="horizontal"
                   height="1px"
                   margin={{ vertical: 'small' }}
                   border={{ color: 'gray-300' }}/>
                <Box id="CdpButtonBox" margin={{ top: 'medium' }} width="256px">
                <Button
                    id="CdpLetsTalkButton"
                    primary color= {'gray-700'}
                    label={<Text fontSize={'18px'} color="base-white" >Let&apos;s Talk</Text>}
                    style={{
                      padding: '8px 16px',
                    }}
                    elevation="small"
                    fill="horizontal"
                    onClick={() => window.location.href = 'mailto:growth@foodmesh.ca'}
                  />
                </Box>
              </Box>
              {/* Column 4: FCDP Results */}
              <Box id="FcdpResults" 
                gridArea="combined" 
                background="custom-light-green" 
                pad="medium" 
                round="small" 
                align="center">
												
                <Box id="FcdpHeadingBox" width="100%" height={{ min: '90px' }} align="center" justify="center">
                  <Text
                    margin="none"
                    // weight="bold"
                    color="green-dark"
                    style={{ fontFamily: 'Arvo, Sans', fontSize: '24px', 
                             fontWeight: 'bold', textAlign: 'center'}}
                  >
                    Farmer + Charity<br/>Diversion Program
                  </Text>
                </Box>
                <Box id="FcdpSectionSpace"
                   flex={false}
                   fill="horizontal"
                   height="1px"
                   margin={{ vertical: 'small' }}
                   border={{ color: 'light-green' }}/>
                <Box
                  id="FcdpMoneySection"
                  width="100%"
                  height={{ min: '120px' }}
                  align="center"
                  justify="center"
                  margin={{ top: 'medium' }}
                  border={{ color: 'light-green', size: 'small' }}
                >
                  <Text
                    id="FcdpMoneyLabel"
                    style={{ fontSize: '24px', fontWeight: 'normal' }}
                    color="green-dark"
                    margin={{ bottom: 'medium' }}
                  >
                    SAVE
                  </Text>
                  <MoneyValueText id="FcdpMoneyValue" color="green-dark">
                    ${formatNumber(fcdpMoney.toFixed(0))} 
                  </MoneyValueText>
                </Box>
                <Box id="FcdpStatsGridBox" width="100%" height="auto" margin={{ top: 'medium' }}>
                  <Grid
                    id="FcdpStatsGrid"
                    rows={['1fr', '1fr', '1fr']}
                    columns={['2fr', '1fr']}
                    gap="small"
                    fill="horizontal"
                  >
                    <Box id="FcdpFoodDiverted" justify="center">
                      <StatValueText color="green-dark">
                        {formatNumber(foodFcdpDiverted.toFixed(0))} kg
                      </StatValueText>
                      <Text 
                      style= {{ fontSize: '12px',
                      fontWeight: 'normal' }} color="green-darker">of food diverted from landfill</Text>
                    </Box>
                    <Box id="FcdpFoodDivertedIcon" align="center" justify="center">
                      <FoodRescue
                        size="52px" 
                        color={customTheme.global.colors['light-green']} 
                      />
                    </Box>
                    <Box id="FcdpCo2eAvoided" justify="center">
                      <StatValueText color="green-dark">
                        {formatNumber(co2eFcdpAvoided.toFixed(0))} kg
                      </StatValueText>
                      <Text 
                      style= {{ fontSize: '12px',
                      fontWeight: 'normal' }} color="green-darker">CO2e emissions avoided</Text>
                    </Box>
                    <Box id="FcdpCo2eAvoidedIcon" align="center" justify="center">
                      <Co2
                        size="52px" 
                        color={customTheme.global.colors['light-green']} 
                      />
                    </Box>
                    <Box id="FcdpMeals" justify="center">
                      <StatValueText color="green-dark">
                        {formatNumber(mealsFcdp.toFixed(0), true)}
                      </StatValueText>
                      <Text 
                      style= {{ fontSize: '12px',
                      fontWeight: 'normal' }} color="green-darker">meals for your community</Text>
                    </Box>
                    <Box id="FcdpMealsIcon" align="center" justify="center">
                      <Meals 
                        size="52px" 
                        color={customTheme.global.colors['light-green']} 
                      />
                    </Box>
                  </Grid>
                </Box>
                <Box id="FcdpSectionSpace"
                   flex={false}
                   fill="horizontal"
                   height="1px"
                   margin={{ vertical: 'small' }}
                   border={{ color: 'light-green' }}/>
                <Box id="FcdpButtonBox" margin={{ top: 'medium' }} width="256px">
                  <Button
                    id="FcdpLetsTalkButton"
                    primary color= {'green-dark'}
                    label={<Text fontSize={'18px'} color="base-white" >Let&apos;s Talk</Text>}
                    style={{
                      padding: '8px 16px',
                    }}
                    elevation="small"
                    fill="horizontal"
                    onClick={() => window.location.href = 'mailto:growth@foodmesh.ca'}
                  />
                </Box>
              </Box>
            </Grid>
          </Box>
        </Box>
      </Main>
    </Grommet>
  );
};

export default ProgramCalculator;